import React, { createContext, useContext, useEffect, useCallback, useMemo } from 'react';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useOrganizations, useClients, useWorkspaces, tenantContext } from '@/shared/api';
import type { Organization, Client, Workspace } from '@/shared/api/types';
import { useAuth } from '@/shared/auth';

// Tenant state store
interface TenantState {
  organizationId: string | null;
  clientId: string | null;
  workspaceId: string | null;
  setOrganizationId: (id: string | null) => void;
  setClientId: (id: string | null) => void;
  setWorkspaceId: (id: string | null) => void;
  clearAll: () => void;
}

const useTenantStore = create<TenantState>()(
  persist(
    (set) => ({
      organizationId: null,
      clientId: null,
      workspaceId: null,
      setOrganizationId: (id) => {
        set({ organizationId: id, clientId: null, workspaceId: null });
        if (id) {
          tenantContext.setOrganizationId(id);
        } else {
          tenantContext.clearOrganizationId();
        }
        tenantContext.clearClientId();
        tenantContext.clearWorkspaceId();
      },
      setClientId: (id) => {
        set({ clientId: id, workspaceId: null });
        if (id) {
          tenantContext.setClientId(id);
        } else {
          tenantContext.clearClientId();
        }
        tenantContext.clearWorkspaceId();
      },
      setWorkspaceId: (id) => {
        set({ workspaceId: id });
        if (id) {
          tenantContext.setWorkspaceId(id);
        } else {
          tenantContext.clearWorkspaceId();
        }
      },
      clearAll: () => {
        set({ organizationId: null, clientId: null, workspaceId: null });
        tenantContext.clearAll();
      },
    }),
    {
      name: 'mip-tenant-state',
      storage: createJSONStorage(() => localStorage),
    }
  )
);

// Context value interface
interface TenantContextValue {
  // Current selections
  organizationId: string | null;
  clientId: string | null;
  workspaceId: string | null;

  // Current entities
  organization: Organization | null;
  client: Client | null;
  workspace: Workspace | null;

  // Available options
  organizations: Organization[];
  clients: Client[];
  workspaces: Workspace[];

  // Loading states
  isLoadingOrganizations: boolean;
  isLoadingClients: boolean;
  isLoadingWorkspaces: boolean;

  // Actions
  selectOrganization: (id: string | null) => void;
  selectClient: (id: string | null) => void;
  selectWorkspace: (id: string | null) => void;
  clearTenant: () => void;

  // Context validity
  hasValidContext: boolean;
  hasOrganization: boolean;
  hasClient: boolean;
  hasWorkspace: boolean;
}

const TenantContext = createContext<TenantContextValue | null>(null);

interface TenantProviderProps {
  children: React.ReactNode;
}

export function TenantProvider({ children }: TenantProviderProps) {
  const { isAuthenticated } = useAuth();
  const {
    organizationId,
    clientId,
    workspaceId,
    setOrganizationId,
    setClientId,
    setWorkspaceId,
    clearAll,
  } = useTenantStore();

  // Fetch organizations
  const {
    data: organizations = [],
    isLoading: isLoadingOrganizations,
  } = useOrganizations();

  // Fetch clients for selected organization
  const {
    data: clients = [],
    isLoading: isLoadingClients,
  } = useClients(organizationId);

  // Fetch workspaces for selected client
  const {
    data: workspaces = [],
    isLoading: isLoadingWorkspaces,
  } = useWorkspaces(clientId);

  // Get current entities
  const organization = useMemo(
    () => organizations.find((o) => o.id === organizationId) ?? null,
    [organizations, organizationId]
  );

  const client = useMemo(
    () => clients.find((c) => c.id === clientId) ?? null,
    [clients, clientId]
  );

  const workspace = useMemo(
    () => workspaces.find((w) => w.id === workspaceId) ?? null,
    [workspaces, workspaceId]
  );

  // Auto-select single options
  useEffect(() => {
    if (!isLoadingOrganizations && organizations.length === 1 && !organizationId) {
      setOrganizationId(organizations[0].id);
    }
  }, [organizations, organizationId, isLoadingOrganizations, setOrganizationId]);

  useEffect(() => {
    if (!isLoadingClients && clients.length === 1 && organizationId && !clientId) {
      setClientId(clients[0].id);
    }
  }, [clients, organizationId, clientId, isLoadingClients, setClientId]);

  useEffect(() => {
    if (!isLoadingWorkspaces && workspaces.length === 1 && clientId && !workspaceId) {
      setWorkspaceId(workspaces[0].id);
    }
  }, [workspaces, clientId, workspaceId, isLoadingWorkspaces, setWorkspaceId]);

  // Validate stored selections against available options
  useEffect(() => {
    if (!isLoadingOrganizations && organizationId) {
      const exists = organizations.some((o) => o.id === organizationId);
      if (!exists && organizations.length > 0) {
        setOrganizationId(null);
      }
    }
  }, [organizations, organizationId, isLoadingOrganizations, setOrganizationId]);

  useEffect(() => {
    if (!isLoadingClients && clientId) {
      const exists = clients.some((c) => c.id === clientId);
      if (!exists && clients.length > 0) {
        setClientId(null);
      }
    }
  }, [clients, clientId, isLoadingClients, setClientId]);

  useEffect(() => {
    if (!isLoadingWorkspaces && workspaceId) {
      const exists = workspaces.some((w) => w.id === workspaceId);
      if (!exists && workspaces.length > 0) {
        setWorkspaceId(null);
      }
    }
  }, [workspaces, workspaceId, isLoadingWorkspaces, setWorkspaceId]);

  // Clear tenant on logout
  useEffect(() => {
    if (!isAuthenticated) {
      clearAll();
    }
  }, [isAuthenticated, clearAll]);

  // Action handlers
  const selectOrganization = useCallback(
    (id: string | null) => {
      setOrganizationId(id);
    },
    [setOrganizationId]
  );

  const selectClient = useCallback(
    (id: string | null) => {
      setClientId(id);
    },
    [setClientId]
  );

  const selectWorkspace = useCallback(
    (id: string | null) => {
      setWorkspaceId(id);
    },
    [setWorkspaceId]
  );

  const clearTenant = useCallback(() => {
    clearAll();
  }, [clearAll]);

  // Context validity checks
  const hasOrganization = !!organization;
  const hasClient = !!client;
  const hasWorkspace = !!workspace;
  const hasValidContext = hasOrganization && hasClient && hasWorkspace;

  const value: TenantContextValue = {
    organizationId,
    clientId,
    workspaceId,
    organization,
    client,
    workspace,
    organizations,
    clients,
    workspaces,
    isLoadingOrganizations,
    isLoadingClients,
    isLoadingWorkspaces,
    selectOrganization,
    selectClient,
    selectWorkspace,
    clearTenant,
    hasValidContext,
    hasOrganization,
    hasClient,
    hasWorkspace,
  };

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

// Hook to use tenant context
export function useTenant() {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}
